import { createContext, useContext, type ReactNode } from "react";
import type { Platform } from "./Platform";
import { useRedesign } from "../flags/useRedesign";
import { platformRef } from "./platformRef";

const PlatformContext = createContext<Platform | null>(null);

export function PlatformProvider({
    platform,
    children,
}: {
    platform: Platform;
    children: ReactNode;
}) {
    return <PlatformContext.Provider value={platform}>{children}</PlatformContext.Provider>;
}

/**
 * The platform for the current React tree. A provider wins when present; otherwise
 * the installed platform (`installPlatform` runs before render on every host, and
 * the Jest setup installs a web test platform), so a component never needs a
 * provider only to read capabilities.
 */
export function usePlatform(): Platform {
    return useContext(PlatformContext) ?? platformRef.current;
}

/** Presentation, not native capability detection. Browser Design v2 uses this same chrome. */
export function useDesktopChrome(): boolean {
    const platform = usePlatform();
    const { enabled } = useRedesign();
    return platform.kind === "desktop" || (platform.kind === "web" && enabled);
}

/** Native appearance preferences stay independent of the web's Design v2 choice. */
export function useAppearancePolicy(): { native: boolean; redesigned: boolean } {
    const platform = usePlatform();
    const { enabled } = useRedesign();
    const native = platform.kind !== "web";
    return { native, redesigned: native || enabled };
}
